import {
    ConsentWorkflow,
    FormDefinitionType,
    IConsentFormConfigurationRequest,
    IEConsentForm,
    Patient,
    RestartConsentForm,
    StartConsentForm
} from '@/api/dtos';
import useRestartConsentForm from '@/hooks/form/useRestartConsentForm';
import useStartConsentForm from '@/hooks/form/useStartConsentForm';
import useInvestigatorLookup, { INVESTIGATOR_LOOKUP_OTHER } from '@/hooks/lookups/useInvestigatorLookup';
import useConsentVersionLookup, { PICF_VERSION_OTHER_ID } from '@/hooks/lookups/useConsentVersionLookup';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2,
    MenuItem,
    TextField
} from '@mui/material';
import { FormContext, FormDefinitionContext, IFormContext, InstitutionContext, PatientContext, useSnackbar } from '@ngt/opms';
import { FC, FormEvent, useCallback, useContext, useState } from 'react';

export type ConsentConfigurationActionType = 'start' | 'restart';

export interface ConsentConfigurationActionProps {
    action: ConsentConfigurationActionType;
    formDefinitionId: FormDefinitionType;
}

type ConsentConfigurationErrors = Partial<Record<
    'workflow' |
    'participantFirstName' |
    'participantLastName' |
    'participantEmailAddress' |
    'assignedInvestigatorUsername' |
    'assignedInvestigatorSpecify' |
    'picfId',
    string
>>;

const picfTypeNames: Partial<Record<FormDefinitionType, string>> = {
    [FormDefinitionType.MainConsentForm]: 'Main',
    [FormDefinitionType.OptionalConsentForm]: 'Tissue',
    [FormDefinitionType.ServicesAustraliaConsentForm]: 'Services',
};

const ConsentConfigurationAction: FC<ConsentConfigurationActionProps> = ({
    action,
    formDefinitionId,
}) => {
    const restarting = action === 'restart';
    const actionLabel = restarting ? 'Restart' : 'Start';
    const { enqueueSnackbar } = useSnackbar();
    const { data: institution } = useContext(InstitutionContext);
    const { data: formDefinition } = useContext(FormDefinitionContext);
    const { data: untypedPatient, refresh: refreshPatient } = useContext(PatientContext);
    const patient = untypedPatient as Patient | undefined;
    const formName = formDefinition?.name ?? 'Consent Form';
    const { data: form, refresh: refreshForm } = useContext(FormContext) as unknown as IFormContext<IEConsentForm>;
    const picfTypeName = picfTypeNames[formDefinitionId];

    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [workflow, setWorkflow] = useState<ConsentWorkflow | ''>('');
    const [participantFirstName, setParticipantFirstName] = useState('');
    const [participantLastName, setParticipantLastName] = useState('');
    const [participantEmailAddress, setParticipantEmailAddress] = useState('');
    const [assignedInvestigatorUsername, setAssignedInvestigatorUsername] = useState('');
    const [assignedInvestigatorSpecify, setAssignedInvestigatorSpecify] = useState('');
    const [picfId, setPicfId] = useState<number | ''>('');
    const [errors, setErrors] = useState<ConsentConfigurationErrors>({});
    const [requestError, setRequestError] = useState<string>();

    const { data: picfLookup, loading: picfVersionsLoading } = useConsentVersionLookup(
        'picfId',
        patient?.institutionCode,
        picfTypeName,
        true
    );
    const { data: investigatorLookup, loading: investigatorsLoading } = useInvestigatorLookup(
        'assignedInvestigatorUsername',
        institution?.code,
        true
    );

    const startConsent = useStartConsentForm({
        revalidateForm: refreshForm,
        refreshPatient
    });
    const restartConsent = useRestartConsentForm({
        revalidateForm: refreshForm,
        refreshPatient
    });

    const close = useCallback(() => {
        if (!saving) {
            setOpen(false);
        }
    }, [saving]);

    const showDialog = useCallback(() => {
        setWorkflow(restarting ? form?.workflow ?? '' : '');
        setParticipantFirstName(restarting ? form?.participantFirstName ?? patient?.firstName ?? '' : patient?.firstName ?? '');
        setParticipantLastName(restarting ? form?.participantLastName ?? patient?.lastName ?? '' : patient?.lastName ?? '');
        setParticipantEmailAddress(restarting ? form?.participantEmailAddress ?? patient?.email ?? '' : patient?.email ?? '');
        setAssignedInvestigatorUsername(restarting ? form?.assignedInvestigatorUsername ?? '' : '');
        setAssignedInvestigatorSpecify(restarting ? form?.assignedInvestigatorSpecify ?? '' : '');
        setPicfId(restarting && form?.workflow === ConsentWorkflow.EConsent ? form?.picfId ?? '' : '');
        setErrors({});
        setRequestError(undefined);
        setOpen(true);
    }, [form, patient, restarting]);

    const validate = useCallback(() => {
        const nextErrors: ConsentConfigurationErrors = {};

        if (workflow === '') {
            nextErrors.workflow = 'Select a consent workflow.';
        }

        if (workflow === ConsentWorkflow.EConsent) {
            if (picfId === '') {
                nextErrors.picfId = 'Select a PICF version.';
            }
            else if (picfId === PICF_VERSION_OTHER_ID) {
                nextErrors.picfId = 'Select an existing PICF version for eConsent.';
            }
            if (!participantFirstName.trim()) {
                nextErrors.participantFirstName = 'First name is required for eConsent.';
            }
            if (!participantLastName.trim()) {
                nextErrors.participantLastName = 'Last name is required for eConsent.';
            }
            if (!participantEmailAddress.trim()) {
                nextErrors.participantEmailAddress = 'Email address is required for eConsent.';
            }
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(participantEmailAddress.trim())) {
                nextErrors.participantEmailAddress = 'Enter a valid email address.';
            }
            if (!assignedInvestigatorUsername) {
                nextErrors.assignedInvestigatorUsername = 'Select an investigator.';
            }
            if (assignedInvestigatorUsername === INVESTIGATOR_LOOKUP_OTHER && !assignedInvestigatorSpecify.trim()) {
                nextErrors.assignedInvestigatorSpecify = 'Specify the investigator name.';
            }
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }, [
        workflow,
        participantFirstName,
        participantLastName,
        participantEmailAddress,
        assignedInvestigatorUsername,
        assignedInvestigatorSpecify,
        picfId
    ]);

    const submit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        setRequestError(undefined);

        if (!validate() || !patient?.id || workflow === '' ||
            (workflow === ConsentWorkflow.EConsent && picfId === '')) {
            return;
        }

        const selectedPicfVersion = workflow === ConsentWorkflow.EConsent && picfId !== ''
            ? picfLookup?.items?.find(item => Number(item.id) === picfId)?.value ??
                (restarting && form?.picfId === picfId ? form.picfVersion : undefined)
            : undefined;

        const configuration: IConsentFormConfigurationRequest = {
            patientId: patient.id,
            formDefinitionId,
            workflow,
            participantFirstName: workflow === ConsentWorkflow.EConsent ? participantFirstName.trim() : undefined,
            participantLastName: workflow === ConsentWorkflow.EConsent ? participantLastName.trim() : undefined,
            participantEmailAddress: workflow === ConsentWorkflow.EConsent ? participantEmailAddress.trim() : undefined,
            assignedInvestigatorUsername: workflow === ConsentWorkflow.EConsent ? assignedInvestigatorUsername : undefined,
            assignedInvestigatorSpecify: workflow === ConsentWorkflow.EConsent && assignedInvestigatorUsername === INVESTIGATOR_LOOKUP_OTHER
                ? assignedInvestigatorSpecify.trim()
                : undefined,
            picfId: workflow === ConsentWorkflow.EConsent && picfId !== '' ? picfId : undefined,
            picfVersion: selectedPicfVersion
        };

        setSaving(true);

        try {
            if (restarting) {
                await restartConsent(new RestartConsentForm(configuration));
            }
            else {
                await startConsent(new StartConsentForm(configuration));
            }

            setOpen(false);
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        {formName} {restarting ? 'Restarted' : 'Started'}
                    </AlertTitle>
                    {formName} was {restarting ? 'restarted' : 'started'} successfully.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            setRequestError(
                error?.responseStatus?.message ??
                error?.message ??
                `${formName} could not be ${restarting ? 'restarted' : 'started'}.`
            );
        }
        finally {
            setSaving(false);
        }
    }, [
        validate,
        patient,
        formDefinitionId,
        workflow,
        participantFirstName,
        participantLastName,
        participantEmailAddress,
        assignedInvestigatorUsername,
        assignedInvestigatorSpecify,
        picfId,
        picfLookup,
        form,
        restarting,
        restartConsent,
        startConsent,
        enqueueSnackbar,
        formName
    ]);

    if ((restarting && !form?.id) || (!restarting && form?.id)) {
        return null;
    }

    const eConsent = workflow === ConsentWorkflow.EConsent;

    return (
        <>
            <Button
                variant="contained"
                color={restarting ? 'warning' : 'success'}
                onClick={showDialog}
                disabled={!patient?.id}
            >
                {actionLabel}
            </Button>

            <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
                <Box component="form" onSubmit={submit} noValidate>
                    <DialogTitle>{actionLabel} {formName}</DialogTitle>
                    <DialogContent>
                        {requestError && <Alert severity="error" sx={{ mb: 2 }}>{requestError}</Alert>}

                        {restarting && (
                            <>
                                Restarting will clear all data and signatures currently stored in this consent form. Are you sure you want to restart this consent form?
                            </>
                        )}

                        <TextField
                            select
                            fullWidth
                            required
                            margin="normal"
                            label="Consent workflow"
                            value={workflow}
                            onChange={(event) => {
                                const nextWorkflow = Number(event.target.value) as ConsentWorkflow;
                                setWorkflow(nextWorkflow);
                                if (nextWorkflow !== ConsentWorkflow.EConsent) {
                                    setPicfId('');
                                }
                                setErrors(current => ({ ...current, workflow: undefined, picfId: undefined }));
                            }}
                            error={Boolean(errors.workflow)}
                            helperText={errors.workflow}
                        >
                            <MenuItem value={ConsentWorkflow.Standard}>Standard</MenuItem>
                            <MenuItem value={ConsentWorkflow.EConsent}>eConsent</MenuItem>
                        </TextField>

                        {eConsent && (
                            <>
                                <Grid2 container spacing={2}>
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            required
                                            margin="normal"
                                            label="Participant first name"
                                            value={participantFirstName}
                                            onChange={(event) => setParticipantFirstName(event.target.value)}
                                            error={Boolean(errors.participantFirstName)}
                                            helperText={errors.participantFirstName}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            required
                                            margin="normal"
                                            label="Participant last name"
                                            value={participantLastName}
                                            onChange={(event) => setParticipantLastName(event.target.value)}
                                            error={Boolean(errors.participantLastName)}
                                            helperText={errors.participantLastName}
                                        />
                                    </Grid2>
                                </Grid2>

                                <TextField
                                    fullWidth
                                    required
                                    type="email"
                                    margin="normal"
                                    label="Participant email address"
                                    value={participantEmailAddress}
                                    onChange={(event) => setParticipantEmailAddress(event.target.value)}
                                    error={Boolean(errors.participantEmailAddress)}
                                    helperText={errors.participantEmailAddress}
                                />

                                <TextField
                                    select
                                    fullWidth
                                    required
                                    margin="normal"
                                    label="PICF version"
                                    value={picfId}
                                    onChange={(event) => {
                                        setPicfId(Number(event.target.value));
                                        setErrors(current => ({ ...current, picfId: undefined }));
                                    }}
                                    disabled={picfVersionsLoading || !picfTypeName}
                                    error={Boolean(errors.picfId)}
                                    helperText={errors.picfId}
                                >
                                    {(picfLookup?.items ?? [])
                                        .filter(item => Number(item.id) !== PICF_VERSION_OTHER_ID)
                                        .map(item => (
                                            <MenuItem key={`${item.id}`} value={Number(item.id)}>{item.value}</MenuItem>
                                        ))}
                                </TextField>

                                <TextField
                                    select
                                    fullWidth
                                    required
                                    margin="normal"
                                    label="Assigned investigator"
                                    value={assignedInvestigatorUsername}
                                    onChange={(event) => setAssignedInvestigatorUsername(event.target.value)}
                                    disabled={investigatorsLoading}
                                    error={Boolean(errors.assignedInvestigatorUsername)}
                                    helperText={errors.assignedInvestigatorUsername}
                                >
                                    {(investigatorLookup?.items ?? []).map(item => (
                                        <MenuItem key={`${item.id}`} value={`${item.id}`}>{item.value}</MenuItem>
                                    ))}
                                </TextField>

                                {assignedInvestigatorUsername === INVESTIGATOR_LOOKUP_OTHER && (
                                    <TextField
                                        fullWidth
                                        required
                                        margin="normal"
                                        label="Investigator name"
                                        value={assignedInvestigatorSpecify}
                                        onChange={(event) => setAssignedInvestigatorSpecify(event.target.value)}
                                        error={Boolean(errors.assignedInvestigatorSpecify)}
                                        helperText={errors.assignedInvestigatorSpecify}
                                    />
                                )}
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={close} disabled={saving}>Cancel</Button>
                        <Button type="submit" variant="contained" color={restarting ? 'warning' : 'primary'} loading={saving}>
                            {actionLabel}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </>
    );
};

export default ConsentConfigurationAction;
